// frontend/src/services/userService.js
import axios from 'axios'

const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

const getAuthHeaders = (token) => ({
	headers: {Authorization: `Bearer ${token}`},
})

/**
 * Updates the current user's settings.
 * @param {string} token - The authentication token.
 * @param {object} settingsData - The settings to update.
 * @param {string} [settingsData.department] - The user's department.
 * @param {string} [settingsData.preferred_greeting] - The user's preferred greeting.
 * @returns {Promise<object>} - A promise that resolves to the updated user settings data.
 */
export const updateUserSettings = async (token, settingsData) => {
	const response = await axios.patch(
		`${API_BASE_URL}/users/me/settings`,
		settingsData,
		getAuthHeaders(token)
	)
	return response.data
}

/**
 * Gets the current user's preferred arcano.
 * @param {string} token - The authentication token.
 * @returns {Promise<string|null>} - A promise that resolves to the preferred arcano ID or null.
 */
export const getUserPreferredArcano = async (token) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/users/me/preferred-arcano`,
			getAuthHeaders(token)
		)
		return response.data.preferred_arcano_id
	} catch (error) {
		console.error('❌ Error fetching preferred arcano:', error)
		// If the new endpoint doesn't exist, return null
		if (error.response?.status === 404) {
			return null
		}
		throw error
	}
}

/**
 * Sets the current user's preferred arcano.
 * @param {string} token - The authentication token.
 * @param {string|null} arcanoId - The arcano ID to set as preferred, or null to clear.
 * @returns {Promise<boolean>} - A promise that resolves to true if successful.
 */
export const setUserPreferredArcano = async (token, arcanoId) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/users/me/preferred-arcano`,
			{preferred_arcano_id: arcanoId},
			getAuthHeaders(token)
		)
		console.log('✅ Successfully set preferred arcano:', response.data)
		return true
	} catch (error) {
		console.error('❌ Error setting preferred arcano:', error)
		// If the new endpoint doesn't exist, fallback to old settings endpoint
		if (error.response?.status === 404) {
			try {
				await updateUserSettings(token, {preferred_arcano_id: arcanoId})
				return true
			} catch (fallbackError) {
				console.error('❌ Settings fallback also failed:', fallbackError)
				return false
			}
		}
		return false
	}
}
